// App
import type React from 'react';
import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import numeral from 'numeral';

// Constellation
import {
  AdornedInput,
  Adornment,
  Button,
  ButtonGroup,
  Heading,
  Input,
  Label,
  ModalDialog,
  Spacer,
  Text,
  TextButton,
} from '@zillow/constellation';

// Components
import PriceCard from './PriceCard';

// Actions
import { handlePMRFilterChange } from 'app/shared/flux/actions/FilterActions';

// Misc / utils
import * as S from './styles';

// Types
import type { State } from 'app/shared/types/State';
import { logError } from '@zg-rentals/log-error';

const ChangePriceModal = ({ close, ...rest }: { close: () => void }) => {
  const dispatch = useDispatch();
  const userProperty = useSelector((state: State) => state.address);
  const priceFromFilter = useSelector((state: State) => state.filter.price);

  const { street, unit, rentZestimate } = userProperty;
  const isCurrentPriceCustom = !!priceFromFilter && priceFromFilter !== rentZestimate;
  const addressText = `${street} ${unit && unit !== ' ' ? ', Unit ' + unit : ''}`;

  const [activeCard, updateactiveCard] = useState(
    isCurrentPriceCustom ? 'custom' : rentZestimate ? 'zestimate' : 'custom',
  );
  const [inputPrice, updateInputPrice] = useState(isCurrentPriceCustom ? numeral(priceFromFilter).format('0,0') : '');
  const [errorMsg, updateErrorMsg] = useState('');

  const handleClickZestimate = () => {
    updateErrorMsg('');
    updateactiveCard('zestimate');
  };

  const handleClickCustom = () => {
    updateactiveCard('custom');
  };

  const handlePriceChange = (e: React.FormEvent<HTMLInputElement>) => {
    // @ts-ignore https://github.com/desktop/desktop/issues/297
    const enteredValue = e.target.value;

    updateErrorMsg('');
    updateInputPrice(enteredValue ? numeral(enteredValue).format('0,0') : '');
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    // @ts-ignore https://github.com/desktop/desktop/issues/297
    if (e.key === 'Enter') {
      // prevents the last used button (the Change price button) from triggering
      e.preventDefault();

      handleSave();
    }
  };

  const handleCancel = () => {
    updateErrorMsg('');
    close();
  };

  const handleSave = () => {
    const priceInput = activeCard === 'zestimate' ? rentZestimate : inputPrice;
    const newPrice = numeral(priceInput).value();

    if (activeCard === 'custom' && newPrice === null) {
      updateErrorMsg('Please enter an amount');
      return;
    }
    const partialFilterObj = { price: newPrice };

    // @ts-ignore
    dispatch(handlePMRFilterChange(partialFilterObj)).catch((err: unknown) => {
      logError({
        error: err,
        errorType: 'Filter',
        context: {
          details: 'Filter: Error handling filter price change',
          root: 'components/ChangePriceModal#handleSave',
        },
      });
    });
    close();
  };

  return (
    <ModalDialog
      {...rest}
      mediaQuery={{ xs: { fullScreen: true }, lg: { fullScreen: false } }}
      header={<Heading level="6">Price</Heading>}
      onClose={handleCancel}
      body={
        <S.Container>
          <Text as="div">
            <strong>{addressText}</strong>
          </Text>
          <Text as="div" marginTop="sm" marginBottom="md">
            {rentZestimate ? (
              <>
                We&#39;ll show you nearby rental properties closest to the price you choose. You can compare prices
                based on the Rent Zestimate®, or enter a custom price.
              </>
            ) : (
              <>Enter a custom price and we&#39;ll show you nearby rental listings closest to the price you choose.</>
            )}
          </Text>
          {rentZestimate && (
            <PriceCard
              description={`The Rent Zestimate for this property is $${numeral(rentZestimate).format('0,0')}/mo`}
              handleClick={handleClickZestimate}
              isActive={activeCard === 'zestimate'}
              title="Use Rent Zestimate"
            />
          )}
          {rentZestimate && (
            <PriceCard
              description="See competing listings at your chosen price point"
              handleClick={handleClickCustom}
              isActive={activeCard === 'custom'}
              title="Enter a Custom Price"
            />
          )}
          {activeCard === 'custom' && (
            <>
              <Label required>Your custom price</Label>
              <AdornedInput
                error={errorMsg !== ''}
                marginTop="xs"
                leftAdornment={<Adornment>$</Adornment>}
                input={
                  <Input
                    placeholder="Enter price here"
                    onChange={handlePriceChange}
                    onKeyDown={handleKeyDown}
                    value={parseInt(inputPrice, 10) === rentZestimate ? '' : inputPrice}
                    required
                  />
                }
                // @ts-ignore
                size="lg"
              />
              {errorMsg !== '' && <S.InputError>{errorMsg}</S.InputError>}
              <Spacer marginTop="xs" marginBottom="sm" />
            </>
          )}
        </S.Container>
      }
      footer={
        <ButtonGroup aria-label="Set price">
          <TextButton onClick={handleCancel}>Cancel</TextButton>
          <Button buttonType="primary" onClick={handleSave}>
            Set price
          </Button>
        </ButtonGroup>
      }
    />
  );
};

export default ChangePriceModal;
